import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export function Container({ className = '', children, ...props }) {
  return (
    <div className={`lp-container ${className}`} {...props}>
      {children}
    </div>
  );
}

// Label that rolls up on hover (duplicate copy is hidden from assistive tech).
export function RollLabel({ children }) {
  return (
    <span className="lp-roll">
      <span>{children}</span>
      <span aria-hidden="true">{children}</span>
    </span>
  );
}

const BUTTON_VARIANTS = {
  dark: 'lp-btn-dark',
  light: 'lp-btn-light',
  outline: 'lp-btn-outline'
};

const BUTTON_SIZES = {
  sm: 'lp-btn-sm',
  md: 'lp-btn-md'
};

export function PillLink({ to, href, variant = 'dark', size = 'md', className = '', children, ...props }) {
  const classes = `${BUTTON_VARIANTS[variant]} ${BUTTON_SIZES[size]} ${className}`;
  const label = <RollLabel>{children}</RollLabel>;

  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {label}
      </Link>
    );
  }

  return (
    <a href={href} className={classes} {...props}>
      {label}
    </a>
  );
}

export function Chip({ icon: Icon, children }) {
  return (
    <li className="lp-chip">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-ink/80" aria-hidden="true" />}
      <span>{children}</span>
    </li>
  );
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);

  return reduced;
}

// Fades content up once it scrolls into view. Renders visible when observers are unavailable.
export function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...props }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`lp-reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...props}
    >
      {children}
    </Tag>
  );
}

// Screens captured from the real ProCTM app, per interface language.
// Intrinsic sizes are the 2x capture sizes, used to reserve layout space.
const SHOT_SIZES = {
  'hero-dashboard': [2880, 1800],
  'hero-clients': [2880, 1800],
  'hero-projects': [2880, 1800],
  'hero-tasks': [2880, 1800],
  'hero-invoices': [2880, 1800],
  'hero-calendar': [2880, 1800],
  'card-crm': [736, 814],
  'card-projects': [736, 654],
  'card-finance': [1688, 1200],
  'feature-crm': [2368, 1800],
  'feature-projects': [2368, 2000],
  'feature-finance': [2368, 1672],
  'overlay-team': [1078, 1182],
  'bento-chat': [1692, 840],
  'bento-portal': [2200, 1520]
};

export function shot(name, language) {
  const [width, height] = SHOT_SIZES[name];
  return {
    src: `/landing/${language === 'ar' ? 'ar' : 'en'}/${name}.webp`,
    width,
    height
  };
}
